import { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { api } from '../services/api';
import { logger } from '../utils/logger';

/**
 * 🎣 REACT HOOK для поиска фильмов
 * 
 * ❓ ЗАЧЕМ НУЖЕН ХУК (а не просто функция в utils)?
 * 1. Управляет СОСТОЯНИЕМ (что пользователь печатает)
 * 2. Делает DEBOUNCING (не спамит API на каждую букву)
 * 3. Кэширует результаты (не повторяет одинаковые запросы)
 * 4. Переиспользуется в разных компонентах
 * 
 * 🔄 КАК РАБОТАЕТ:
 * Пользователь печатает "spider" → состояние обновляется на каждую букву
 * → через 500ms после остановки печати → делается API запрос
 * → результаты кэшируются и возвращаются компоненту
 * 
 * @param initialQuery - Начальный поисковый запрос (опционально)
 * @returns Объект с состоянием поиска и функциями управления
 */
export const useMovieSearch = (initialQuery: string = '') => {
  // 📝 СОСТОЯНИЕ 1: Что пользователь печатает прямо сейчас
  // Обновляется на каждую букву, связано с input полем
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  
  // 📝 СОСТОЯНИЕ 2: Задержанная версия поискового запроса
  // Обновляется только через 500ms после остановки печати
  // Именно по этому запросу делается API вызов
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(initialQuery);

  // ⏱️ DEBOUNCING ЭФФЕКТ
  // Зачем? Чтобы не делать API запрос на каждую букву!
  // Пример: пользователь печатает "spider man"
  // Без debouncing: 10 API запросов (s, sp, spi, spid, ...)
  // С debouncing: 1 API запрос через 500ms после "spider man"
  useEffect(() => {
    // Создаем таймер на 500ms
    const timer = setTimeout(() => {
      // Через 500ms обновляем debouncedSearchQuery
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    // ⚠️ ВАЖНО: очищаем предыдущий таймер при новом вводе
    // Если пользователь продолжает печатать, старый таймер отменяется
    return () => clearTimeout(timer);
  }, [searchQuery]); // Запускается каждый раз при изменении searchQuery

  // 🌐 API ЗАПРОС с помощью React Query
  // React Query - это библиотека для управления серверным состоянием
  // Она автоматически кэширует результаты, показывает loading состояния
  const { 
    data: searchResults,        // Результаты поиска от API
    isLoading: searching,       // true во время загрузки
    error: searchError          // Ошибка если что-то пошло не так
  } = useQuery(
    // 🔑 КЛЮЧ КЭШИРОВАНИЯ: ['search-movies', 'spider man']
    // React Query кэширует результаты по этому ключу
    // Если тот же запрос повторится - вернет из кэша
    ['search-movies', debouncedSearchQuery],
    
    // 📡 ФУНКЦИЯ ЗАПРОСА: выполняется только когда нужны новые данные
    () => {
      logger.searchStarted(debouncedSearchQuery);
      return api.get('/movies/search', { 
        params: { query: debouncedSearchQuery } 
      }).then(res => {
        logger.searchResults(res.data.results.length);
        return res.data.results; // Возвращаем только массив фильмов
      });
    },
    
    // ⚙️ НАСТРОЙКИ ЗАПРОСА
    {
      // Запрос выполняется только если запрос длиннее 2 символов
      enabled: debouncedSearchQuery.length > 2,
      
      // Кэшировать результаты на 5 минут
      // Повторный поиск "spider" в течение 5 минут вернет кэш
      staleTime: 5 * 60 * 1000,
      
      // Обработчик ошибок
      onError: (error) => {
        logger.error('Search query failed', error);
      }
    }
  );

  // 📤 ВОЗВРАЩАЕМ ОБЪЕКТ с состоянием и функциями
  // Компонент получит все необходимое для работы с поиском
  return {
    // 📊 СОСТОЯНИЕ
    searchQuery,                              // Текущий ввод пользователя
    searchResults: searchResults || [],       // Результаты поиска (массив фильмов)
    searching,                                // Идет ли загрузка
    searchError,                              // Ошибка если есть
    
    // 🎛️ ФУНКЦИИ УПРАВЛЕНИЯ
    setSearchQuery,                           // Обновить поисковый запрос
    clearSearch: () => setSearchQuery(''),    // Очистить поиск
    
    // 🧮 ВЫЧИСЛЯЕМЫЕ ЗНАЧЕНИЯ (для удобства в компонентах)
    hasResults: searchResults && searchResults.length > 0,  // Есть ли результаты
    showResults: searchQuery.length > 2,                    // Показывать ли блок результатов
  };
};
